import { EngagementBuyerActionState } from '../../slices/marketplace/timeline/models/engagement-buyer-action-state.model';
import { EngagementData } from '../../slices/marketplace/timeline/models/engagement-data.model';
import { ActionDocumentType } from '../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import { EngagementDocumentSource } from '../../slices/marketplace/timeline/models/enums/engagement-document-source';
import { TimelineActionStatus } from '../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import { TimelineEventStatus } from '../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { TimelineParty } from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import {
    NdaTermSelectionOptionsList,
    TimelineActionType,
} from '../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineAction } from '../../slices/marketplace/timeline/models/timeline-actions.model';
import { TimelineEvent } from '../../slices/marketplace/timeline/models/timeline-event.model';

export const isEventActionClickable = (
    eventid: string,
    isReadOnly: boolean,
    engagementData: EngagementData,
    events: TimelineEvent[],
    party: TimelineParty,
    buyerActionState?: EngagementBuyerActionState[]
): boolean => {
    if (isReadOnly && party !== TimelineParty.Observer) {
        return false;
    }

    const event = events.find((event) => event.id === eventid);
    if (event == null) {
        return false;
    }

    if (!event?.linkedAction) return false;

    const matchingParty = event.party == party;
    const sellerViewingSeller = matchingParty && party === TimelineParty.Seller;
    const isEventUpcoming = event.status === TimelineEventStatus.Upcoming;
    const isBrokerOrObserver = party === TimelineParty.Broker || party === TimelineParty.Observer;

    if (
        event.linkedAction?.actionType ==
        TimelineActionType.SellerEngagementDocument
    ) {
        const canEngagementDocBeViewed =
            party === TimelineParty.VetValue ||
            isBrokerOrObserver ||
            (sellerViewingSeller &&
                !isEventUpcoming &&
                doesEventActionDocDetailsContainContent(
                    event?.linkedAction?.referenceItemId,
                    engagementData
                ));
        return canEngagementDocBeViewed;
    }

    if (
        event.linkedAction?.actionType == TimelineActionType.BuyerReviewDocument
    ) {
        const canBuyerReviewDocBeViewed =
            (party === TimelineParty.VetValue ||
                (party === TimelineParty.Buyer && !isEventUpcoming)) &&
            isBuyerReviewDocReady(event?.linkedAction, engagementData, events);

        return canBuyerReviewDocBeViewed;
    }

    if (
        NdaTermSelectionOptionsList.some(
            (option) => option.value === event.linkedAction?.actionType
        )
    ) {
        return party === TimelineParty.VetValue || !isEventUpcoming;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerTeaserApproval
    ) {
        const canTeaserBeViewed = isEventUpcoming
            ? party === TimelineParty.VetValue || isBrokerOrObserver
            : party !== TimelineParty.Buyer;

        return canTeaserBeViewed;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerBuyerSelectionIdNda
    ) {
        if (party === TimelineParty.Seller && !isEventUpcoming) return true;
        if (party === TimelineParty.VetValue || isBrokerOrObserver) return true;

        const targetAction = buyerActionState?.find(
            (p) => p.actionId === event.linkedAction.id
        );
        return !!targetAction;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerBuyerSelectionDetailedFinancialModel
    ) {
        if (party === TimelineParty.Seller && !isEventUpcoming) return true;
        if (party === TimelineParty.VetValue || isBrokerOrObserver) return true;

        const targetAction = buyerActionState?.find(
            (p) => p.actionId === event.linkedAction.id
        );

        return !!targetAction;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerDataAndDocumentRequest
    ) {
        return (
            (matchingParty && !isEventUpcoming) ||
            party === TimelineParty.VetValue ||
            isBrokerOrObserver
        );
    }

    if (event.linkedAction?.actionType === TimelineActionType.SellerProfile) {
        return (
            (matchingParty && !isEventUpcoming) ||
            party === TimelineParty.VetValue ||
            isBrokerOrObserver
        );
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.BuyerExpressionOfInterestWithRange
    ) {
        const isRangeProvided =
            buyerActionState?.find((p) => p.actionId === event.linkedAction?.id)
                ?.status === TimelineActionStatus.Completed;
        return isEventUpcoming ? false : matchingParty || isRangeProvided;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.VetValueBuyerSelectionAndTeaserPosting
    ) {
        return isEventUpcoming
            ? false
            : matchingParty || isBrokerOrObserver;
    }

    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerProvideMeetingAvailability
    ) {
        return isEventUpcoming
            ? false
            : party === TimelineParty.Seller ||
                  party === TimelineParty.VetValue ||
                  isBrokerOrObserver;
    }
    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerBuyerAgreeOnMeetingTimes
    ) {
        return isEventUpcoming
            ? false
            : party === TimelineParty.Seller ||
                  party === TimelineParty.Buyer ||
                  party === TimelineParty.VetValue ||
                  isBrokerOrObserver;
    }
    if (
        event.linkedAction?.actionType ===
        TimelineActionType.SellerBuyerMeetingsPeriod
    ) {
        return isEventUpcoming
            ? false
            : party === TimelineParty.Seller ||
                  party === TimelineParty.Buyer ||
                  party === TimelineParty.VetValue ||
                  isBrokerOrObserver;
    }

    return isEventUpcoming
        ? false
        : matchingParty || party === TimelineParty.VetValue || isBrokerOrObserver;
};

const doesEventActionDocDetailsContainContent = (
    docId: string,
    engagementData: EngagementData
): boolean => {
    const doc = engagementData.documentsList.find((doc) => doc.id === docId);

    if (doc?.type == ActionDocumentType.ForSign) {
        return true;
    }

    if (
        doc?.type == ActionDocumentType.DocumentWithFeedback &&
        doc?.source == EngagementDocumentSource.Questionnaire
    ) {
        return true;
    }

    const result = doc?.fileName != '';
    return result;
};

const isBuyerReviewDocReady = (
    action: TimelineAction,
    engagementData: EngagementData,
    events: TimelineEvent[]
) => {
    if (action?.actionType !== TimelineActionType.BuyerReviewDocument)
        return false;

    const doc = engagementData.documentsList.find(
        (doc) => doc.id === action?.referenceItemId
    );

    const isLinked = !!action?.actionReferenceId;
    const isLinkedReady =
        isLinked &&
        events.find(
            (e) =>
                e.linkedAction &&
                e.linkedAction?.id == action?.actionReferenceId
        )?.status === TimelineEventStatus.Completed;

    const result = doc?.isPrepared && (!isLinked || isLinkedReady);

    return result;
};
